import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const FEEDBACK_MS = 2000;

// Hidden-textarea copy for when the async Clipboard API is missing or
// refused. Focus is handed back afterwards so keyboard users stay on the
// button they pressed.
const copyWithExecCommand = (text) => {
  const previouslyFocused = document.activeElement;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand('copy')) throw new Error('Copy command was rejected');
  } finally {
    document.body.removeChild(textarea);
    previouslyFocused?.focus?.();
  }
};

// navigator.clipboard only exists in secure contexts (https or localhost), so
// a plain-http deployment always needs the fallback.
const writeToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      // Permission denied or document not focused — try the fallback.
    }
  }
  copyWithExecCommand(text);
};

// small sits inline in a table cell; medium sits beside an ID on the mobile
// cards — icon close to the text size, padding for a ~28px tap target.
const SIZES = {
  small: { icon: 14, p: 0.25 },
  medium: { icon: 16, p: 0.75 },
};

// Copy icon that briefly turns into a tick with a "Copied" tooltip instead of a toast.
const CopyButton = ({ value, label, size = 'small' }) => {
  const dims = SIZES[size] || SIZES.small;
  const [status, setStatus] = useState('idle'); // 'idle' | 'copied' | 'failed'
  const resetTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  const handleCopy = async (event) => {
    // Don't also trigger a click handler on the row or card around it.
    event.stopPropagation();
    clearTimeout(resetTimerRef.current);
    try {
      await writeToClipboard(String(value));
      setStatus('copied');
    } catch (error) {
      console.error('Copy failed:', error);
      setStatus('failed');
    }
    resetTimerRef.current = setTimeout(() => setStatus('idle'), FEEDBACK_MS);
  };

  const title = status === 'copied' ? 'Copied' : status === 'failed' ? "Couldn't copy" : `Copy ${label}`;

  return (
    <Tooltip title={title} placement="top" disableInteractive>
      <IconButton
        size="small"
        aria-label={title}
        onClick={handleCopy}
        sx={{
          p: dims.p,
          color: status === 'copied' ? 'success.main' : 'text.secondary',
          '&:hover': { color: status === 'copied' ? 'success.main' : 'primary.main' },
        }}
      >
        {status === 'copied'
          ? <CheckIcon sx={{ fontSize: dims.icon }} />
          : <ContentCopyIcon sx={{ fontSize: dims.icon }} />}
      </IconButton>
    </Tooltip>
  );
};

export default CopyButton;
